/* global globalThis */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { fetchMemberOverview } from "../../api/priority";
import { Box, Typography, Button } from "@mui/material";
import NoRoleIcon from "../../assets/no-role-icon.svg";
import MainLayout from "../../layouts/MainLayout";

const AccessRestricted = ({ children }) => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formatForbiddenMessage = (error) => {
    const data = error?.response?.data;
    let message =
      data?.message ??
      (typeof data === "string" ? data : null) ??
      "You don’t have permission to view this member.\nThis member is outside the access assigned to your role.";

    if (typeof message === "string") {
      message = message.replace(/^\s*Forbidden\s*[:\-–—]?\s*/i, "");
    }

    if (
      message === "Unauthorized access to member" ||
      (typeof message === "string" &&
        message.includes("You don’t have permission"))
    ) {
      message =
        "You don’t have permission to view this member.\nThis member is outside the access assigned to your role.";
    }

    return message;
  };

  useEffect(
    () => {
      const checkAccess = async () => {
        if (!memberId) {
          setLoading(false);

          return;
        }
        try {
          await fetchMemberOverview(memberId);

          setLoading(false);
        } catch (error) {
          if (error.response?.status === 403) {
            const message = formatForbiddenMessage(error);
            setErrorMessage(
              typeof message === "string" ? message : JSON.stringify(message),
            );
            setErrorPopupOpen(true);
            setLoading(false);
          } else {
            setLoading(false);
          }
        }
      };

      void checkAccess();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberId, navigate],
  );

  const handleGoBack = () => {
    if (
      globalThis.window.history.state &&
      typeof globalThis.window.history.state.idx === "number" &&
      globalThis.window.history.state.idx > 0
    ) {
      navigate(-1);
    } else {
      navigate("/members/member-outreach");
    }
  };

  if (loading && !location.state) {
    return null;
  }

  if (errorPopupOpen) {
    return (
      <MainLayout hideSearch={true}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 50px)",
            backgroundColor: "#F7F8FB",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              padding: "30px 24px",
              textAlign: "center",
              maxWidth: "480px",
              width: "100%",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <img
                src={NoRoleIcon}
                alt="No Role Icon"
                style={{ width: 68, height: 68 }}
              />
            </Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: "#0E0D09" }}
              mb={1}
            >
              Access Restricted
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#4E4B44", whiteSpace: "pre-line" }}
            >
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={handleGoBack}
              sx={{
                mt: 3,
                backgroundColor: "#403c6f",
                "&:hover": { backgroundColor: "#2e2b52" },
              }}
            >
              Go Back
            </Button>
          </Box>
        </Box>
      </MainLayout>
    );
  }

  return children;
};

export default AccessRestricted;

AccessRestricted.propTypes = {
  children: PropTypes.node.isRequired,
};
